import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, ENDPOINTS, DEFAULT_STORE_ID } from '../config';
import { useAuth } from '../contexts/AuthContext';
import './AddItemToStore.css';

interface Currency {
  currencyId: number;
  currencyName: string;
}

const AddItemToStore: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const storeId = DEFAULT_STORE_ID;

  const [step, setStep] = useState<'category' | 'item' | 'store-item'>('category');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Category form
  const [categoryForm, setCategoryForm] = useState({
    categoryName: '',
    categoryNameEn: '',
    description: '',
    icon: '',
    color: '#059669',
    displayOrder: 0,
    isActive: true
  });

  // Item form
  const [itemForm, setItemForm] = useState({
    itemName: '',
    itemCode: '',
    itemBarCode: '',
    description: '',
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    slug: '',
    isActive: true,
    categoryId: 0
  });

  // Store Item form
  const [storeItemForm, setStoreItemForm] = useState({
    price: 0,
    stockQuantity: 0,
    minStockLevel: 0,
    maxStockLevel: 0,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 0,
    videoUrl: '',
    currencyId: 0,
    minimumOrderQuantity: 1,
    leadTimeDays: 30,
    isExternalOrder: false
  });

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [createdCategoryId, setCreatedCategoryId] = useState<number | null>(null);
  const [createdItemId, setCreatedItemId] = useState<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchCurrencies();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (createdCategoryId) {
      // Intentionally left empty or removed unused fetchItems
    }
  }, [createdCategoryId]);

  const fetchCurrencies = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CURRENCIES}`);
      if (response.ok) {
        const data = await response.json();
        setCurrencies(data);
      }
    } catch (err) {
      console.error('Error fetching currencies:', err);
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.ADD_CATEGORY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(categoryForm)
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedCategoryId(data.categoryId);
        setItemForm(prev => ({ ...prev, categoryId: data.categoryId }));
        setStep('item');
      } else {
        const errorData = await response.json();
        setError(errorData.message || t('createCategoryFailed') as string);
      }
    } catch (err: any) {
      setError(t('createCategoryError') as string);
      console.error('Error creating category:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.ADD_ITEM}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...itemForm,
          specifications: [],
          itemImages: []
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedItemId(data.itemId);
        setStep('store-item');
      } else {
        const errorData = await response.json();
        setError(errorData.message || t('createItemFailed') as string);
      }
    } catch (err: any) {
      setError(t('createItemError') as string);
      console.error('Error creating item:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStoreItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!createdItemId) {
      setError(t('mustCreateItemFirst') as string);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.ADD_STORE_ITEM(storeId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...storeItemForm,
          itemId: createdItemId
        })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || t('addItemToStoreFailed') as string);
      }
    } catch (err: any) {
      setError(t('addItemToStoreError') as string);
      console.error('Error adding item to store:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('addNewItem')} - MIC Green Store</title>
      </Helmet>

      <Container className="add-item-container">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="add-item-card">
              <Card.Header className="text-center">
                <h2>
                  <i className="fas fa-plus-circle me-2"></i>
                  {t('addNewItemToStore')}
                </h2>
                <div className="step-indicator">
                  <span className={step === 'category' ? 'active' : step === 'item' || step === 'store-item' ? 'completed' : ''}>
                    <i className="fas fa-tag"></i> {t('category')}
                  </span>
                  <span className={step === 'item' ? 'active' : step === 'store-item' ? 'completed' : ''}>
                    <i className="fas fa-box"></i> {t('item')}
                  </span>
                  <span className={step === 'store-item' ? 'active' : ''}>
                    <i className="fas fa-store"></i> {t('storeInfo')}
                  </span>
                </div>
              </Card.Header>
              <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{t('itemAddedSuccess')}</Alert>}

                {/* Step 1: Create Category */}
                {step === 'category' && (
                  <Form onSubmit={handleCategorySubmit}>
                    <h4 className="mb-4">{t('step1AddCategory')}</h4>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('categoryName')} <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={categoryForm.categoryName}
                            onChange={(e) => setCategoryForm({ ...categoryForm, categoryName: e.target.value })}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('categoryNameEn')}</Form.Label>
                          <Form.Control
                            type="text"
                            value={categoryForm.categoryNameEn}
                            onChange={(e) => setCategoryForm({ ...categoryForm, categoryNameEn: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('description')}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={categoryForm.description}
                        onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                      />
                    </Form.Group>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('icon')}</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="fas fa-box"
                            value={categoryForm.icon}
                            onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('color')}</Form.Label>
                          <Form.Control
                            type="color"
                            value={categoryForm.color}
                            onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('displayOrder')}</Form.Label>
                          <Form.Control
                            type="number"
                            value={categoryForm.displayOrder}
                            onChange={(e) => setCategoryForm({ ...categoryForm, displayOrder: parseInt(e.target.value) || 0 })}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-between">
                      <Button variant="secondary" onClick={() => navigate('/')}>
                        {t('cancel')}
                      </Button>
                      <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? <Spinner size="sm" /> : t('nextAddItem')}
                      </Button>
                    </div>
                  </Form>
                )}

                {/* Step 2: Create Item */}
                {step === 'item' && (
                  <Form onSubmit={handleItemSubmit}>
                    <h4 className="mb-4">{t('step2AddItem')}</h4>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('itemName')} <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={itemForm.itemName}
                            onChange={(e) => setItemForm({ ...itemForm, itemName: e.target.value })}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('itemCode')} <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={itemForm.itemCode}
                            onChange={(e) => setItemForm({ ...itemForm, itemCode: e.target.value })}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('description')}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={itemForm.description}
                        onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                      />
                    </Form.Group>
                    <div className="d-flex justify-content-between">
                      <Button variant="secondary" onClick={() => setStep('category')}>
                        {t('previous')}
                      </Button>
                      <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? <Spinner size="sm" /> : t('nextStoreInfo')}
                      </Button>
                    </div>
                  </Form>
                )}

                {/* Step 3: Add to Store */}
                {step === 'store-item' && (
                  <Form onSubmit={handleStoreItemSubmit}>
                    <h4 className="mb-4">{t('step3StoreInfo')}</h4>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('price')} <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            value={storeItemForm.price}
                            onChange={(e) => setStoreItemForm({ ...storeItemForm, price: parseFloat(e.target.value) || 0 })}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('currency')}</Form.Label>
                          <Form.Select
                            value={storeItemForm.currencyId}
                            onChange={(e) => setStoreItemForm({ ...storeItemForm, currencyId: parseInt(e.target.value) || 0 })}
                          >
                            <option value={0}>{t('selectCurrency')}</option>
                            {currencies.map(currency => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('stockQuantity')}</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            value={storeItemForm.stockQuantity}
                            onChange={(e) => setStoreItemForm({ ...storeItemForm, stockQuantity: parseFloat(e.target.value) || 0 })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('minStockLevel')}</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            value={storeItemForm.minStockLevel}
                            onChange={(e) => setStoreItemForm({ ...storeItemForm, minStockLevel: parseFloat(e.target.value) || 0 })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('maxStockLevel')}</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            value={storeItemForm.maxStockLevel}
                            onChange={(e) => setStoreItemForm({ ...storeItemForm, maxStockLevel: parseFloat(e.target.value) || 0 })}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('minOrderQuantity')}</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            value={storeItemForm.minimumOrderQuantity}
                            onChange={(e) => setStoreItemForm({ ...storeItemForm, minimumOrderQuantity: parseFloat(e.target.value) || 1 })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('leadTimeDays')}</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            value={storeItemForm.leadTimeDays}
                            onChange={(e) => setStoreItemForm({ ...storeItemForm, leadTimeDays: parseInt(e.target.value) || 30 })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('displayOrder')}</Form.Label>
                          <Form.Control
                            type="number"
                            value={storeItemForm.displayOrder}
                            onChange={(e) => setStoreItemForm({ ...storeItemForm, displayOrder: parseInt(e.target.value) || 0 })}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Check
                            type="checkbox"
                            label={t('available')}
                            checked={storeItemForm.isAvailable}
                            onChange={(e) => setStoreItemForm({ ...storeItemForm, isAvailable: e.target.checked })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Check
                            type="checkbox"
                            label={t('featured')}
                            checked={storeItemForm.isFeatured}
                            onChange={(e) => setStoreItemForm({ ...storeItemForm, isFeatured: e.target.checked })}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('videoUrl')}</Form.Label>
                      <Form.Control
                        type="url"
                        value={storeItemForm.videoUrl}
                        onChange={(e) => setStoreItemForm({ ...storeItemForm, videoUrl: e.target.value })}
                      />
                    </Form.Group>
                    <div className="d-flex justify-content-between">
                      <Button variant="secondary" onClick={() => setStep('item')}>
                        {t('previous')}
                      </Button>
                      <Button variant="success" type="submit" disabled={loading}>
                        {loading ? <Spinner size="sm" /> : t('addItemToStoreBtn')}
                      </Button>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AddItemToStore;

